import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Chip,
  Avatar,
} from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import API_MIDDLEWARE from "../../redux/api/middleware";
import { CLIENT_REPORT_URL } from "../../redux/api/config";
import { useDispatch } from "react-redux";
import { setErrorMessage } from "../../redux/actions/appActions";
import { APP_CUSTOM_ERROR } from "../../constants/AppVarConstants";
import ClientReportFilters from "./ClientReportFilters";
import { dateComparator } from "./sortComparators";
import { exportToExcel } from "./reportUtils";
import { buildClientReportPayload } from "../../redux/actions/b2bActions";
import React from "react";

interface ClientReportData {
  company_id: number;
  company_name: string;
  email: string | null;
  phone_number: string | null;
  city: string | null;
  state: string | null;
  industry: string;
  status: string;
  active_jobs_count: number;
  created_at: string;
  id?: string;
  logo_image_path: string | null;
}

const ClientReport = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<ClientReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const skipNextEffectRef = React.useRef(false);

  // Filter states: custom range (start/end) or year / month(s)
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  // Status chip styling function 
  const getStatusChip = (status: string) => {
    const statusConfig: {
      [key: string]: {
        color: string;
        backgroundColor: string;
        label: string;
      };
    } = {
      ACTIVE: {
        color: '#4CAF50',
        backgroundColor: '#E8F5E8',
        label: 'Active',
      },
      INACTIVE: {
        color: '#F44336',
        backgroundColor: '#FFEBEE',
        label: 'Inactive',
      },
      PENDING: {
        color: '#FF9800',
        backgroundColor: '#FFF3E0',
        label: 'Pending',
      },
    };

    const config = statusConfig[status] || {
      color: '#666',
      backgroundColor: '#F5F5F5',
      label: status,
    };

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.backgroundColor,
          color: config.color,
          fontWeight: 500,
          border: `1px solid ${config.color}20`,
          '&:hover': {
            backgroundColor: config.backgroundColor,
          },
        }}
      />
    );
  };

  useEffect(() => {
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }
    if (hasSubmitted) {
      fetchClientReport(paginationModel.page, paginationModel.pageSize);
    }
  }, [hasSubmitted, paginationModel.page, paginationModel.pageSize]);

  const columns: GridColDef[] = [
    {
        field: "logo_image_path",
        headerName: "Logo",
        flex: 0.6,
        minWidth: 80,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box display="flex" justifyContent="center">
            <Avatar
              src={params.value || ""}
              alt="Company Logo"
              sx={{ width: 40, height: 40, bgcolor: "#eee" }}
              variant="rounded"
            />
          </Box>
        ),
      },
    {
      field: "company_name",
      headerName: "Company Name",
      flex: 1.2,
      minWidth: 130,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 160,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "phone_number",
      headerName: "Phone Number",
      flex: 1.1,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "city",
      headerName: "City",
      flex: 0.8,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "state",
      headerName: "State",
      flex: 0.8,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "industry",
      headerName: "Industry",
      flex: 1,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.9,
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const status = params.row.status;
        return getStatusChip(status);
      },
    },
    {
      field: "active_jobs_count",
      headerName: "Active Jobs",
      flex: 0.9,
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
    {
      field: "created_at",
      headerName: "Created Date",
      flex: 1.2,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      sortComparator: dateComparator,
    },
  ];

  const fetchClientReport = async (
    page: number,
    fetchSize: number,
    filtersOverride?: { startDate: string | null; endDate: string | null; year: string; months: number[] }
  ) => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    const useStart = filtersOverride?.startDate ?? startDate;
    const useEnd = filtersOverride?.endDate ?? endDate;
    const useYear = filtersOverride?.year ?? selectedYear;
    const useMonths = filtersOverride?.months ?? selectedMonths;

    setLoading(true);
    setData([]);
    try {
      const payload = buildClientReportPayload(
        {
          startDate: useStart,
          endDate: useEnd,
          year: useYear,
          months: useMonths.length > 0 ? useMonths : undefined,
        },
        false,
        page,
        fetchSize
      );

      const response = await API_MIDDLEWARE(
        CLIENT_REPORT_URL,
        "POST",
        { "Content-Type": "application/json" },
        payload,
        undefined,
        true,
        true,
        abortControllerRef.current.signal
      );

      const { records, totalRecords } = response?.result || {};
      
      // Add unique IDs to each row for DataGrid
      const dataWithIds = records?.map((record: ClientReportData, index: number) => ({
        ...record,
        id: `${record.company_id}-${index}`,
      })) || [];
      setLoading(false);
      setData(dataWithIds);
      setTotalRecords(totalRecords || 0);
    } catch (err: any) {
      // Don't show error for aborted requests
      if (err.code === 20 || err.name === 'AbortError') {
        console.log('Request was cancelled');
        return;
      }
      if (err.code !== 401) {
        setLoading(false);
        dispatch(setErrorMessage(err?.errorMsg ?? APP_CUSTOM_ERROR));
      }
    } 
  };

  const handleSubmit = () => {
    skipNextEffectRef.current = true; // avoid duplicate fetch from effect
    setHasSubmitted(true);
    setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
    // Pass current filter state so API is called with selected year/month (avoids stale closure)
    fetchClientReport(0, paginationModel.pageSize, {
      startDate,
      endDate,
      year: selectedYear,
      months: selectedMonths,
    });
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedYear("");
    setSelectedMonths([]);
    setHasSubmitted(false);
    setTotalRecords(0);
    setData([]);
    setPaginationModel({ page: 0, pageSize: 50 });
  };

  const handleDownloadExcel = async () => {
    if (data.length === 0) return;

    setDownloadingExcel(true);
    try {
      const payload = buildClientReportPayload(
        { startDate, endDate, year: selectedYear, months: selectedMonths.length > 0 ? selectedMonths : undefined },
        true
      );

      const response = await API_MIDDLEWARE(
        CLIENT_REPORT_URL,
        "POST",
        { "Content-Type": "application/json" },
        payload,
        undefined,
        true,
        true
      );

      const { records } = response?.result || {};

      if (!records || records.length === 0) {
        dispatch(setErrorMessage("No data available to download"));
        setDownloadingExcel(false);
        return;
      }

      const exportData = records.map((row: ClientReportData) => {
        return {
          "Company Name": row.company_name,
          Email: row.email,
          "Phone Number": row.phone_number,
          City: row.city,
          State: row.state,
          Industry: row.industry,
          Status: row.status,
          "Active Jobs": row.active_jobs_count,
          "Created Date": row.created_at,
        };
      });

      exportToExcel({
        data: exportData,
        filename: "Client_Report",
        sheetName: "Client Report",
        maxColumnWidth: 25,
      });

      setDownloadingExcel(false);
    } catch (err: any) {
      if (err.code !== 401) {
        dispatch(setErrorMessage(err?.errorMsg ?? APP_CUSTOM_ERROR));
      }
      setDownloadingExcel(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
      {/* Header with Title and Download Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
          pr: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "600",
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          Client Report
        </Typography>
        <Button
          startIcon={!downloadingExcel ? <DownloadIcon /> : undefined}
          variant="contained"
          disabled={data.length === 0 || loading || downloadingExcel}
          onClick={handleDownloadExcel}
          sx={{
            backgroundColor: "#756DE9",
            color: "#fff",
            borderRadius: "100px",
            padding: "10px 20px",
            "&:hover": {
              backgroundColor: "#5a4dbf",
            },
            "&:disabled": {
              backgroundColor: "#E0E0E0",
              color: "#999999",
            },
            textTransform: "capitalize",
            fontSize: "0.95rem",
            whiteSpace: "nowrap",
            flexShrink: 0,
            minWidth: "fit-content",
          }}
        >
          {downloadingExcel ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "#fff" }} />
              Downloading...
            </Box>
          ) : (
            "Download Excel"
          )}
        </Button>
      </Box>

      {/* Filters Section */}
      <ClientReportFilters
        startDate={startDate}
        endDate={endDate}
        selectedYear={selectedYear}
        selectedMonths={selectedMonths}
        hasSubmitted={hasSubmitted}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onYearChange={setSelectedYear}
        onMonthsChange={setSelectedMonths}
        onSubmit={handleSubmit}
        onClearFilters={handleClearFilters}
      />

      {/* Data Grid */}
      {hasSubmitted ? (
        <Box
          sx={{
            height: 600,
            width: "100%",
            maxWidth: "100%",
            overflow: "auto",
            pr: 2,
            "& .MuiDataGrid-root": {
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#756DE9 !important",
              color: "#fff !important",
              fontSize: "14px",
              fontWeight: "700",
              minHeight: "56px !important",
              maxHeight: "56px !important",
              lineHeight: "56px !important",
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "700",
                color: "#fff !important",
                overflow: "visible",
                whiteSpace: "normal",
                lineHeight: "1.2",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#756DE9 !important",
              },
              "& .MuiDataGrid-iconSeparator": {
                color: "rgba(255, 255, 255, 0.5)",
              },
              "& .MuiDataGrid-sortIcon": {
                color: "#fff !important",
                opacity: 1,
              },
              "& .MuiDataGrid-menuIcon": {
                color: "#fff !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "#fff !important",
              },
            },
            "& .MuiDataGrid-cell": {
              fontSize: "14px",
              borderBottom: "1px solid #F0F0F0",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#F5F5FF",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #E0E0E0",
              backgroundColor: "#F8F8F8",
            },
          }}
        >
          <DataGrid
            rows={data}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={totalRecords}
            paginationMode="server"
            loading={loading}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Typography sx={{ color: "#999", fontSize: "16px" }}>
                    No data available
                  </Typography>
                </Box>
              ),
              loadingOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress size={40} sx={{ color: "#756DE9" }} />
                </Box>
              ),
            }}
            sx={{
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                background: "#F5F5F5",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                background: "#C1C1C1",
                borderRadius: "4px",
                "&:hover": {
                  background: "#A8A8A8",
                },
              },
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            height: 600,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#F8F8F8",
            borderRadius: "8px",
            border: "1px solid #E0E0E0",
          }}
        >
          <Typography sx={{ color: "#999", fontSize: "16px" }}>
            Use filters above (custom date range or year / month) and click Apply to view client report
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ClientReport;
